import { useGetContragents } from '@/shared/api'
import { EditPencilIcon, TrashSVGIcon } from '@/shared/assets'
import {
    ContragentCreateModal,
    ContragentUpdateModal,
} from '@/shared/components/ord/ContragentModal'
import { CheckIcon as CheckIconSVG } from '@/shared/icons'
import { DisableTooltip, ShadcnButton } from '@/shared/ui'
import { preventDefault } from '@/true-fsd/shared/utils/react'
import {
    PopoverPortal,
    Popover as RadixPopover,
    PopoverContent as RadixPopoverContent,
    PopoverTrigger as RadixPopoverTrigger,
} from '@radix-ui/react-popover'
import { ORDContragentRole } from '@synapex/prisma/client'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import { ContragentResponse } from '@synapex/shared2/gateway-rest-api'
import { WithOptional } from '@synapex/tools/typescript'
import cl from 'clsx'
import { CheckIcon, PlusIcon, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { RemoveScroll } from 'react-remove-scroll'
import { ContragentBadge } from './ContragentBadge'
import classes from './ContragentSelector.module.scss'

type ContragentCardProps = {
    onSelectContragent: (contragent: ContragentResponse | null) => void
    currentContragentId?: number | null
    error?: boolean
    setError?: (error: boolean) => void
    roleType: ORDContragentRole
    allowEdit?: boolean
    tooltipMessage: string
} & WithOptional<WorkspaceWithLinkDescriptor, 'link_id'>

const ROLE_LABELS: Record<ORDContragentRole, string> = {
    publisher: 'РР',
    advertiser: 'РД',
    agency: 'РА',
    ors: 'ОРС',
}

const ContragentCard: React.FC<ContragentCardProps> = ({
    onSelectContragent,
    setError,
    roleType,
    currentContragentId = null,
    allowEdit = true,
    tooltipMessage,

    workspace_type,
    workspace_id,
    link_id,
}) => {
    const [selectedContragent, setSelectedContragent] =
        useState<ContragentResponse | null>(null)

    const [open, setOpen] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)

    const [selectedContragentIdToDelete, setSelectedContragentIdToDelete] =
        useState(false)

    const { data: contragents } = useGetContragents({
        workspace_type,
        workspace_id,
        roles: [roleType],
    })

    useEffect(() => {
        if (currentContragentId && contragents?.length) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedContragent(
                contragents.find(
                    (contragent) =>
                        contragent.ord_contragent_id === currentContragentId
                ) || null
            )
        }
    }, [contragents, currentContragentId])

    function handleEditContragent() {
        if (!selectedContragent) {
            return
        }
        setIsEditMode(true)
        setOpen(true)
    }

    function handleOpenModal(open: boolean) {
        setOpen(open)
        setIsEditMode(false)
    }

    function handleCreateContragentClick() {
        setIsEditMode(false)
        setOpen(true)
    }

    function handleDiselectContragent() {
        setSelectedContragentIdToDelete(false)
        setSelectedContragent(null)

        onSelectContragent(null)
    }

    const isVerifiedInVkORd =
        selectedContragent?.vk_ord_status &&
        selectedContragent.vk_ord_status[
            selectedContragent.vk_ord_status.length - 1
        ]?.last_sync_status === 'verified'

    const isCanChangeContragent = !selectedContragent?.hasContracts
    const tooltipCanChangeText = 'Нельзя менять контрагента если есть закупы'

    const isPublisher = roleType === 'publisher'

    return (
        <div className="flex gap-[10px]">
            {isEditMode && selectedContragent ? (
                <ContragentUpdateModal
                    {...{
                        workspace_type,
                        workspace_id,
                        link_id,
                    }}
                    onOpenChange={handleOpenModal}
                    open={open}
                    source={selectedContragent}
                    roleType={roleType}
                />
            ) : (
                <ContragentCreateModal
                    {...{
                        workspace_type,
                        workspace_id,
                        link_id,
                    }}
                    onOpenChange={handleOpenModal}
                    open={open}
                    roleType={roleType}
                />
            )}

            {selectedContragent && selectedContragentIdToDelete ? (
                <div className="flex h-[30px] w-[302px] items-center justify-end gap-2 rounded-lg border border-stroke p-2">
                    <span className="text-[13px]">Удалить?</span>
                    <div className="flex items-center">
                        <div
                            onClick={handleDiselectContragent}
                            className="cursor-pointer text-blue-primary"
                        >
                            <CheckIcon className="h-4 w-4" />
                        </div>
                        <div
                            onClick={() =>
                                setSelectedContragentIdToDelete(false)
                            }
                            className="cursor-pointer"
                        >
                            <XIcon className="h-4 w-4" />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="w-[302px]">
                    <ContragentSelector
                        onSelect={(contragent) => {
                            setSelectedContragent(contragent)
                            setError?.(false)
                            onSelectContragent(contragent)
                        }}
                        inn={selectedContragent?.inn}
                        contragents={contragents || []}
                        onCreateContragentClick={handleCreateContragentClick}
                        disabled={
                            !allowEdit ||
                            (!isCanChangeContragent && !isPublisher)
                        }
                        locked={!isCanChangeContragent && !isPublisher}
                        tooltipMessage={
                            !isCanChangeContragent && !isPublisher
                                ? tooltipCanChangeText
                                : tooltipMessage
                        }
                        roleLabel={ROLE_LABELS[roleType]}
                    />
                </div>
            )}

            {selectedContragent && !selectedContragentIdToDelete && (
                <div className="flex items-center gap-[6px]">
                    {(!isVerifiedInVkORd || isCanChangeContragent) && (
                        <DisableTooltip
                            enabled={!allowEdit}
                            message={tooltipMessage}
                            className="flex items-center"
                        >
                            <ShadcnButton
                                variant="ghost"
                                onClick={handleEditContragent}
                                className="h-fit p-0"
                                disabled={!allowEdit}
                            >
                                <EditPencilIcon className="h-4 w-4" />
                            </ShadcnButton>
                        </DisableTooltip>
                    )}

                    <DisableTooltip
                        enabled={
                            !allowEdit ||
                            (!isCanChangeContragent && !isPublisher)
                        }
                        message={
                            !isCanChangeContragent
                                ? tooltipCanChangeText
                                : tooltipMessage
                        }
                        className="flex items-center"
                    >
                        <ShadcnButton
                            className="h-fit p-0"
                            variant="ghost"
                            disabled={
                                !allowEdit ||
                                (!isCanChangeContragent && !isPublisher)
                            }
                            onClick={() =>
                                setSelectedContragentIdToDelete(true)
                            }
                        >
                            <TrashSVGIcon />
                        </ShadcnButton>
                    </DisableTooltip>
                </div>
            )}
        </div>
    )
}

function ContragentSelector({
    onSelect,
    inn = '',
    contragents,
    onCreateContragentClick,
    disabled,
    locked = false,
    tooltipMessage,
    roleLabel = 'РР',
}: {
    onSelect?: (contragent: ContragentResponse) => void
    inn?: string
    contragents: ContragentResponse[]
    onCreateContragentClick?: () => void
    disabled?: boolean
    locked?: boolean
    tooltipMessage?: string
    roleLabel?: string
}) {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')

    const selectedContragent =
        contragents.find((contragent) => contragent.inn === inn) || null

    const onChangeSearch: React.ChangeEventHandler<HTMLInputElement> = (evt) =>
        setSearch(evt.target.value)

    const filteredContragents = contragents.filter((contragent) => {
        if (!search) return true
        const query = search.toLowerCase()
        return (
            contragent.inn.toLowerCase().includes(query) ||
            contragent.name.toLowerCase().includes(query) ||
            false
        )
    })

    return (
        <div className={classes.root}>
            {open && (
                <input
                    placeholder="Поиск по ИНН или названию"
                    autoFocus
                    value={search}
                    onChange={onChangeSearch}
                    className={classes.input}
                    disabled={disabled}
                />
            )}
            <ListPopover
                open={open}
                onOpenChange={(newOpen) => {
                    if (!locked) {
                        setOpen(newOpen)
                    }
                }}
                onSelect={(contragent) => {
                    onSelect?.(contragent)
                    setOpen(false)
                }}
                selectedContragent={selectedContragent}
                filteredContragents={filteredContragents}
                disabled={disabled}
                locked={locked}
                tooltipMessage={tooltipMessage}
                onCreateContragentClick={onCreateContragentClick}
                roleLabel={roleLabel}
            />
        </div>
    )
}

type ListPopoverProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSelect: (contragent: ContragentResponse) => void
    selectedContragent: ContragentResponse | null
    filteredContragents: ContragentResponse[]
    disabled?: boolean
    locked?: boolean
    tooltipMessage?: string
    onCreateContragentClick?: () => void
    roleLabel: string
}

const ListPopover = ({
    open,
    onOpenChange,
    onSelect,
    selectedContragent,
    filteredContragents,
    disabled,
    locked = false,
    tooltipMessage,
    onCreateContragentClick,
    roleLabel,
}: ListPopoverProps) => {
    if (locked) {
        return (
            <DisableTooltip enabled message={tooltipMessage} asChild>
                <div className="grow">
                    {!open && (
                        <ContragentBadge
                            value={selectedContragent}
                            className="opacity-50"
                        />
                    )}
                </div>
            </DisableTooltip>
        )
    }

    return (
        <RadixPopover open={open} onOpenChange={onOpenChange}>
            <RadixPopoverTrigger
                className={cl('grow', { ['h-0']: open })}
                disabled={disabled}
            >
                {!open && (
                    <ContragentBadge
                        value={selectedContragent}
                        className={cl('hover:bg-gray-100', {
                            ['opacity-50']: disabled,
                        })}
                    />
                )}
            </RadixPopoverTrigger>

            <PopoverPortal>
                <RadixPopoverContent
                    className={classes.popover}
                    align={'start'}
                    sideOffset={2}
                    avoidCollisions={false}
                    onOpenAutoFocus={preventDefault}
                    style={{ width: 302 }}
                >
                    {open && (
                        <RemoveScroll className={classes.list}>
                            <div
                                onClick={onCreateContragentClick}
                                className={classes.item}
                            >
                                <div className="flex flex-col">
                                    Добавить нового {roleLabel}
                                </div>
                                <div className={'grow'} />
                                <PlusIcon className={classes.icon} />
                            </div>
                            {filteredContragents.length > 0 ? (
                                filteredContragents.map((contragent) => (
                                    <ListItem
                                        key={contragent.inn}
                                        contragent={contragent}
                                        selected={
                                            selectedContragent?.inn ===
                                            contragent.inn
                                        }
                                        onSelect={onSelect}
                                    />
                                ))
                            ) : (
                                <div className={classes.emptyState}>
                                    Контрагенты не найдены
                                </div>
                            )}
                        </RemoveScroll>
                    )}
                </RadixPopoverContent>
            </PopoverPortal>
        </RadixPopover>
    )
}

type ListItemProps = {
    contragent: ContragentResponse
    selected: boolean
    onSelect: (contragent: ContragentResponse) => void
}

const ListItem = ({ contragent, selected, onSelect }: ListItemProps) => {
    const onClick = () => onSelect(contragent)

    return (
        <div
            onClick={onClick}
            className={cl(classes.item, { [classes.selected]: selected })}
        >
            <div className="flex flex-col">
                <div className={classes.name}>{contragent.name}</div>
                <div className={classes.inn}>ИНН: {contragent.inn}</div>
            </div>
            <div className={'grow'} />
            {selected && <CheckIconSVG className={classes.icon} />}
        </div>
    )
}

export default ContragentCard
